import { Validators } from '@angular/forms';

import {
    CommitmentKpi,
    GoalModel,
    GoalStatus,
} from '../../coaching-base/general';
import {
    CmpsFormField,
    CmpsFormFields,
} from '../../coaching-observation/experimental.form';
import { TranslationKeyMap } from '../coaching-commitment.i18n';
import { CloseGoalCommitmentOption } from '../commitment-close-goal/close-goal-modal.commitment.component';

export interface GoalFormFields extends CmpsFormFields {
    GOAL_ID: CmpsFormField;
    METRIC: CmpsFormField;
    GOAL: CmpsFormField;
    IMPROVEMENT: CmpsFormField;
    DATE: CmpsFormField;
    RANK_ORDER: CmpsFormField;
    SHARED: CmpsFormField;
    STARTING_GOAL_ID: CmpsFormField;
    ACTUAL: CmpsFormField;
    ASSOCIATE_TO_PARENT_FOCUS: CmpsFormField;
    STATUS_ID: CmpsFormField;
    IS_ORIGINAL_GOAL: CmpsFormField;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const GoalFormFields: GoalFormFields = {
    GOAL_ID: {
        name: 'goalId',
        label: '',
    },
    METRIC: {
        name: 'metric',
        label: '',
        placeholder: TranslationKeyMap._9516_SELECT_KPI___,
        validators: [Validators.required],
        validationMsgs: {
            required: TranslationKeyMap._9513_METRIC_IS_REQUIRED_,
        },
    },
    ACTUAL: {
        name: 'actual',
        label: '',
    },
    GOAL: {
        name: 'goal',
        label: '',
        validationMsgs: {
            requiredWhen:
                TranslationKeyMap._9514_PLEASE_ENTER_A_FUTURE_ACTUAL_GOAL_OR_IMPROVEMENT_EXPECTED_,
        },
    },
    IMPROVEMENT: {
        name: 'improvement',
        label: '',
    },
    DATE: {
        name: 'date',
        label: '',
        validators: [Validators.required],
        validationMsgs: {
            required:
                TranslationKeyMap._9515_EXPECTED_ACHIEVEMENT_DATE_IS_REQUIRED_,
            ngbDate: TranslationKeyMap._9512_INVALID_DATE_,
        },
    },
    RANK_ORDER: {
        name: 'rankOrder',
        label: '',
    },
    SHARED: {
        name: 'shared',
        label: '',
    },
    STARTING_GOAL_ID: {
        name: 'startingGoalId',
        label: '',
    },
    TRACKING_ID: {
        name: 'tracking_id',
        label: '',
    },
    ASSOCIATE_TO_PARENT_FOCUS: {
        name: 'associateToParentFocus',
        label: '',
    },
    STATUS_ID: {
        name: 'statusId',
        label: '',
    },
    IS_ORIGINAL_GOAL: {
        name: 'isOriginalGoal',
        label: '',
    },
};

export interface GoalFormValue {
    goalId: number; // keep references
    metric: number;
    goal: number;
    date: Date;
    rankOrder?: number; // keep references
    shared?: boolean;
    commitmentId?: number;
    eventId?: number;

    /** only persisted for drafts */
    associateToParentFocus?: boolean;

    // calculated/always readonly
    improvement?: number;
    initialActual?: number;
    oppActiveDays?: number;
    daysToAchievement?: number;
    goalCreator?: string;
    startPercentToGoal?: number;
    improvementSinceLastGoal?: number;
    overallImprovement?: number;
    actual?: number;
    target?: number;
    metricName?: string;
    percentToTarget?: number;

    // non-form
    startingGoalId?: number;

    // for ui highlighting/selection
    tracking_id?: number;

    statusId?: GoalStatus;

    isOriginalGoal: boolean;
    diffItemId: string;
}

export function goalFormValueTransformer(model: GoalModel): GoalFormValue {
    return {
        goalId: model.goalId,
        metric: model.metricId,
        goal: simpleRound(model.goalValue),
        improvement: null,
        date: model.estimatedCompletionDate,
        rankOrder: model.rankOrder,
        associateToParentFocus: model.associateToParentFocus,
        shared: model.shared,
        startingGoalId: model.startingGoalId,
        isOriginalGoal: undefined,
        diffItemId: model.diffItemId,
    };
}

export const commitmentKPIToGoalFormValueTransformer = (
    model: CommitmentKpi,
): GoalFormValue => {
    return {
        goalId: model.goalId,
        metric: model.metricId,
        metricName: model.metricName,
        goal: model.goalValue,
        date: model.estimatedCompletionDate
            ? new Date(model.estimatedCompletionDate)
            : null,
        startingGoalId: model.startingGoalId,
        actual: model.actual,
        target: model.target,
        percentToTarget: model.percentToTarget,
        commitmentId: model.commitmentId,
        eventId: model.eventId,
        shared: model.shared,
        associateToParentFocus: model.associateToParentFocus,
        statusId: model.statusId,

        // calculated/always readonly
        improvement: model.improvementRating,
        initialActual: model.initialActual,
        goalCreator: model.createdBy,
        oppActiveDays: model.oppActiveDays,
        daysToAchievement: model.daysToGoalExpiry,
        improvementSinceLastGoal: model.improvementSinceLastGoal,
        overallImprovement: model.overallImprovement,

        // for ui highlighting/selection
        tracking_id: model.tracking_id,

        isOriginalGoal: model.isOriginalGoal,
        diffItemId: model.diffItemId,
    };
};

export const goalFormToCommitmentKPITransformer = (
    model: GoalFormValue,
    updatedGoal?: CommitmentKpi,
): CommitmentKpi => {
    if (updatedGoal) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return {
            ...updatedGoal,
            goalValue: model.goal,
            estimatedCompletionDate: new Date(model.date.setHours(0, 0, 0, 0)),
            tracking_id: model.tracking_id,
            associateToParentFocus: model.associateToParentFocus,
            actual: model.actual,
            target: model.target,
            percentToTarget: model.percentToTarget,
            shared: model.shared,
            daysToGoalExpiry: model.daysToAchievement,
            updatedOn: new Date(),
            isOriginalGoal: undefined,
            diffItemId: model.diffItemId,
        } as CommitmentKpi;
    } else {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return {
            goalId: model.goalId,
            metricId: model.metric,
            goalValue: model.goal,
            estimatedCompletionDate: new Date(model.date.setHours(0, 0, 0, 0)),
            startingGoalId: model.startingGoalId,
            metricName: model.metricName,
            tracking_id: !model.goalId ? Math.random() : model.tracking_id, // if new goal, set a new tracking_id
            associateToParentFocus: model.associateToParentFocus,
            actual: model.actual,
            target: model.target,
            percentToTarget: model.percentToTarget,
            shared: model.shared,
            daysToGoalExpiry: model.daysToAchievement,
            statusId: model.statusId,
            updatedOn: new Date(),
            isOriginalGoal: undefined,
            diffItemId: model.diffItemId,
        } as CommitmentKpi;
    }
};

export function simpleRound(value: number): number {
    if (value == null) {
        return null;
    }
    return Math.round(value * 100) / 100;
}

export interface CommitmentCloseGoalValue {
    closeOption: CloseGoalCommitmentOption;
    commitmentKpi: CommitmentKpi;
}
